import type { BoxProps } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/react";
import Cloudflare from "assets/logos/brands/cloudflare.svg?react";
import Discovery from "assets/logos/brands/discovery.svg?react";
import Flowco from "assets/logos/brands/flowco.svg?react";
import GeneralGalactic from "assets/logos/brands/general-galactic.svg?react";
import Glooko from "assets/logos/brands/glooko.svg?react";
import HuggingFace from "assets/logos/brands/hugging-face.svg?react";
import Kempower from "assets/logos/brands/kempower.svg?react";
import Orca from "assets/logos/brands/orca.svg?react";
import PancakeSwap from "assets/logos/brands/pancake-swap.svg?react";
import PfeiferUndLangen from "assets/logos/brands/pfeifer-und-langen.svg?react";
import Polymarket from "assets/logos/brands/polymarket.svg?react";
import Raytheon from "assets/logos/brands/raytheon.svg?react";
import Rockwool from "assets/logos/brands/rockwool.svg?react";
import SchneiderElectric from "assets/logos/brands/schneider-electric.svg?react";
import Speedcast from "assets/logos/brands/speedcast.svg?react";
import TitanAmerica from "assets/logos/brands/titan-america.svg?react";
import Toyota from "assets/logos/brands/toyota.svg?react";
import VideoJet from "assets/logos/brands/videojet.svg?react";
import WaterBridge from "assets/logos/brands/waterbridge.svg?react";
import type { ComponentType, SVGProps } from "react";

export type LogoMarqueeItem = {
  Logo: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
};

/** Customer brand logos scrolled by the marquee (e.g. on the sign-up page). */
export const CUSTOMER_LOGOS: LogoMarqueeItem[] = [
  { Logo: Toyota, label: "Toyota" },
  { Logo: Rockwool, label: "Rockwool" },
  { Logo: VideoJet, label: "VideoJet" },
  { Logo: Flowco, label: "Flowco" },
  { Logo: TitanAmerica, label: "Titan America" },
  { Logo: SchneiderElectric, label: "Schneider Electric" },
  { Logo: PfeiferUndLangen, label: "Pfeifer & Langen" },
  { Logo: Glooko, label: "Glooko" },
  { Logo: Raytheon, label: "Raytheon" },
  { Logo: WaterBridge, label: "WaterBridge" },
  { Logo: Polymarket, label: "Polymarket" },
  { Logo: Kempower, label: "Kempower" },
  { Logo: Speedcast, label: "Speedcast" },
  { Logo: GeneralGalactic, label: "General Galactic" },
  { Logo: Discovery, label: "Discovery" },
  { Logo: HuggingFace, label: "Hugging Face" },
  { Logo: Cloudflare, label: "Cloudflare" },
  { Logo: PancakeSwap, label: "PancakeSwap" },
  { Logo: Orca, label: "Orca" },
];

export type LogoMarqueeProps = {
  /** Duration of one full loop. */
  speed?: string;
  /** Height of each logo. */
  logoHeight?: string;
  /** Horizontal gap between logos (Chakra spacing token). */
  gap?: string;
} & BoxProps;

export const LogoMarquee = ({
  speed = "40s",
  logoHeight = "32px",
  gap = "16",
  ...boxProps
}: LogoMarqueeProps) => {
  const row = (rowKey: string) => (
    <Flex gap={gap} alignItems="center" flexShrink={0} pr={gap} aria-hidden>
      {CUSTOMER_LOGOS.map(({ Logo, label }, i) => (
        <Box
          key={`${rowKey}-${i}`}
          h={logoHeight}
          color="fg.default"
          aria-label={label}
          sx={{
            "& svg": {
              height: "100%",
              width: "auto",
              display: "block",
            },
          }}
        >
          <Logo />
        </Box>
      ))}
    </Flex>
  );

  return (
    <Box
      overflow="hidden"
      sx={{
        "@keyframes logoMarqueeScroll": {
          from: { transform: "translate3d(0, 0, 0)" },
          to: { transform: "translate3d(-50%, 0, 0)" },
        },
        maskImage:
          "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
      }}
      {...boxProps}
    >
      <Flex
        w="max-content"
        animation={`logoMarqueeScroll ${speed} linear infinite`}
        _hover={{ animationPlayState: "paused" }}
      >
        {row("a")}
        {row("b")}
      </Flex>
    </Box>
  );
};
